import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "America/Sao_Paulo";

const MONTHS_SHORT = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/** "2026-09" a partir de uma data ISO, sempre no fuso de São Paulo. */
export const monthKeyOf = (value) => {
  if (!value) return null;
  const d = dayjs(value).tz(TZ);
  if (!d.isValid()) return null;
  return d.format("YYYY-MM");
};

/** "2026-09" -> "Set/26" (rótulo curto do eixo X). */
export const monthLabel = (key) => {
  if (!key) return "";
  const [year, month] = key.split("-").map(Number);
  if (!year || !month) return "";
  return `${MONTHS_SHORT[month - 1]}/${String(year).slice(2)}`;
};

const monthIndex = (key) => {
  const [year, month] = key.split("-").map(Number);
  return year * 12 + (month - 1);
};

const keyFromIndex = (index) => {
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
};

/** Lista de meses de `from` até `to`, inclusive. Ordem inválida devolve []. */
export const monthRange = (from, to) => {
  if (!from || !to) return [];
  const start = monthIndex(from);
  const end = monthIndex(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];

  const keys = [];
  for (let i = start; i <= end; i++) keys.push(keyFromIndex(i));
  return keys;
};

/**
 * Janela imediatamente anterior, do mesmo tamanho. Um filtro de Mai→Ago
 * (4 meses) compara com Jan→Abr.
 */
export const previousRange = (from, to) => {
  const size = monthRange(from, to).length;
  if (!size) return null;
  const end = monthIndex(from) - 1;
  return { from: keyFromIndex(end - size + 1), to: keyFromIndex(end) };
};

/** Mês anterior a `key`. Usado para pegar a base ativa no início do mês. */
export const previousMonth = (key) => {
  if (!key) return null;
  const index = monthIndex(key);
  return Number.isNaN(index) ? null : keyFromIndex(index - 1);
};

const inRange = (key, from, to) => !!key && key >= from && key <= to; // "YYYY-MM" ordena como string

/**
 * Data de desativação. O cadastro não tem campo próprio, então usamos o
 * `updatedAt` das empresas com `enable = false`. Empresa ativa não tem data.
 */
export const disabledAtOf = (company) => {
  if (!company || company.enable) return null;
  return company.updatedAt || null;
};

/** Empresas criadas dentro da janela (a coorte do período). */
export const cohortOf = (companies, from, to) =>
  (companies || []).filter((c) => inRange(monthKeyOf(c.createdAt), from, to));

/** Empresa que já saiu: inativa com data de desativação conhecida. */
export const isDisabled = (company) => !!disabledAtOf(company);

/**
 * Saiu DENTRO da janela informada (pela data de desativação). Usado só como
 * recorte da safra — nunca para contar saída de safra antiga no período.
 */
export const disabledInRange = (company, from, to) =>
  inRange(monthKeyOf(disabledAtOf(company)), from, to);

/** Dias de vida: do cadastro até a desativação. `null` se ainda está ativa. */
export const lifetimeDays = (company) => {
  const disabledAt = disabledAtOf(company);
  if (!company?.createdAt || !disabledAt) return null;
  const created = dayjs(company.createdAt).tz(TZ).startOf("day");
  const closed = dayjs(disabledAt).tz(TZ).startOf("day");
  if (!created.isValid() || !closed.isValid()) return null;
  const diff = closed.diff(created, "day");
  return diff < 0 ? 0 : diff;
};

export const median = (values) => {
  const list = (values || [])
    .filter((v) => typeof v === "number")
    .sort((a, b) => a - b);
  if (!list.length) return null;
  const middle = Math.floor(list.length / 2);
  return list.length % 2
    ? list[middle]
    : Math.round(((list[middle - 1] + list[middle]) / 2) * 10) / 10;
};

export const average = (values) => {
  const list = (values || []).filter((v) => typeof v === "number");
  if (!list.length) return null;
  return Math.round((list.reduce((a, b) => a + b, 0) / list.length) * 10) / 10;
};

const pct = (part, total) => (total ? Math.round((part / total) * 1000) / 10 : 0);

/**
 * Base ativa no FIM do mês `key`: criadas até ali menos desativadas até ali.
 *
 * Só funciona porque a desativação é datável via `updatedAt`. Assume que
 * empresa desativada não foi reativada depois — o cadastro não guarda esse
 * histórico.
 */
export const activeBaseAt = (companies, key) => {
  if (!key) return 0;
  let criadas = 0;
  let desativadas = 0;

  (companies || []).forEach((company) => {
    const created = monthKeyOf(company.createdAt);
    if (!created || created > key) return;
    criadas += 1;

    const disabled = monthKeyOf(disabledAtOf(company));
    if (disabled && disabled <= key) desativadas += 1;
  });

  return criadas - desativadas;
};

/**
 * Bloco 1 — aquisição: entradas do período, comparativo com o período
 * anterior e o tamanho da base.
 */
export const buildSummary = (companies, from, to) => {
  const months = monthRange(from, to);
  const cohort = cohortOf(companies, from, to);

  const previous = previousRange(from, to);
  const previousCohort = previous
    ? cohortOf(companies, previous.from, previous.to)
    : [];

  const criadasAteTo = (companies || []).filter((c) => {
    const key = monthKeyOf(c.createdAt);
    return !!key && key <= to;
  });

  const baseInicio = activeBaseAt(companies, previousMonth(from));
  const baseFim = activeBaseAt(companies, to);

  return {
    months: months.length,
    novas: cohort.length,
    novasAnterior: previousCohort.length,
    deltaPct: previousCohort.length
      ? Math.round(
          ((cohort.length - previousCohort.length) / previousCohort.length) *
            1000,
        ) / 10
      : null,
    mediaMes: months.length
      ? Math.round((cohort.length / months.length) * 10) / 10
      : 0,
    criadasTotal: criadasAteTo.length,
    baseAtivaFim: baseFim,
    baseAtivaInicio: baseInicio,
    // Crescimento do estoque: diferença entre fechamento e abertura. É a única
    // leitura que legitimamente mistura safras, porque mede a base, não a
    // performance da aquisição do período.
    crescimentoBase: baseFim - baseInicio,
    periodoAnterior: previous,
  };
};

/**
 * Bloco 2 — retenção da safra: das empresas que ENTRARAM no período, quantas
 * já saíram.
 *
 * A contabilidade é fechada na safra de propósito: empresa que entrou em outro
 * período e saiu dentro desta janela NÃO conta como saída aqui. Misturar as
 * duas coisas contamina a leitura da aquisição — a perda da base como um todo
 * se lê pelo `crescimentoBase` do resumo.
 */
export const buildCohortRetention = (companies, from, to) => {
  const cohort = cohortOf(companies, from, to);
  const saidas = cohort.filter(isDisabled);
  const lifetimes = saidas.map(lifetimeDays).filter((v) => typeof v === "number");

  return {
    novas: cohort.length,
    desativadas: saidas.length,
    ativasHoje: cohort.length - saidas.length,
    pctPerda: pct(saidas.length, cohort.length),
    pctRetencao: pct(cohort.length - saidas.length, cohort.length),
    // Recorte: entrou E saiu dentro da própria janela. O restante das saídas
    // é de empresa que entrou no período e caiu depois do fim dele.
    saiuNoPeriodo: saidas.filter((c) => disabledInRange(c, from, to)).length,
    vidaMedia: average(lifetimes),
    vidaMediana: median(lifetimes),
  };
};

/**
 * Série mensal por MÊS DE ENTRADA: quantas entraram e, dessas, quantas já
 * saíram. As saídas são sempre da própria safra do mês.
 *
 * `baseAtiva` é o estoque de empresas ativas no fim do mês (todas as safras) e
 * vive num GRÁFICO SEPARADO: plotar estoque e fluxo no mesmo eixo exigiria dois
 * eixos Y, cujo alinhamento é arbitrário e inventa correlação. Derivados por mês
 * (% de perda, sobrevivência) ficam em `buildCohortTable`, não aqui.
 */
export const buildMonthlySeries = (companies, from, to) => {
  const months = monthRange(from, to);
  if (!months.length) return [];

  const byMonth = {};
  months.forEach((key) => {
    byMonth[key] = { novas: 0, desativadas: 0 };
  });

  (companies || []).forEach((company) => {
    const created = monthKeyOf(company.createdAt);
    if (!created || !byMonth[created]) return;
    byMonth[created].novas += 1;
    if (isDisabled(company)) byMonth[created].desativadas += 1;
  });

  return months.map((key) => {
    const bucket = byMonth[key];
    return {
      month: key,
      label: monthLabel(key),
      novas: bucket.novas,
      desativadas: bucket.desativadas,
      baseAtiva: activeBaseAt(companies, key),
    };
  });
};

/**
 * Bloco 3 — coorte: por mês de entrada, quantas seguem ativas hoje e quanto
 * tempo durou quem saiu.
 */
export const buildCohortTable = (companies, from, to) => {
  const months = monthRange(from, to);
  if (!months.length) return [];

  const grouped = {};
  months.forEach((key) => {
    grouped[key] = [];
  });
  (companies || []).forEach((company) => {
    const key = monthKeyOf(company.createdAt);
    if (key && grouped[key]) grouped[key].push(company);
  });

  return months.map((key) => {
    const list = grouped[key];
    const ativas = list.filter((c) => c.enable);
    const desativadas = list.filter((c) => !c.enable);

    return {
      id: key,
      month: key,
      label: monthLabel(key),
      entradas: list.length,
      ativasHoje: ativas.length,
      pctSobrevivencia: pct(ativas.length, list.length),
      desativadas: desativadas.length,
      vidaMediana: median(
        desativadas.map(lifetimeDays).filter((v) => typeof v === "number"),
      ),
    };
  });
};

/**
 * Nome do arquivo sugerido na impressão. O navegador usa o `document.title`
 * como nome default do PDF, então a página troca o título antes de imprimir.
 * Sem espaço nem acento: alguns navegadores truncam no primeiro espaço.
 */
export const printFileName = (from, to) => {
  const base = "aquisicao-empresas";
  const janela = monthRange(from, to);
  if (!janela.length) return base;

  const slug = (key) => monthLabel(key).replace("/", "").toLowerCase();
  const inicio = slug(from);
  const fim = slug(to);

  return inicio === fim ? `${base}_${inicio}` : `${base}_${inicio}-${fim}`;
};

/** Mês corrente e 1º de janeiro do ano corrente, defaults do filtro. */
export const defaultRange = (now) => {
  const ref = now ? dayjs(now).tz(TZ) : dayjs().tz(TZ);
  return { from: `${ref.year()}-01`, to: ref.format("YYYY-MM") };
};
